import React from 'react';
import { Amount } from '@akropolis-web/primitives';

import { MRT_ColumnDef, amountToAccessorValue, ToggleLowSupplyFilterButton } from 'components/MRT';
import {
  FormattedAmount,
  InfoTooltip,
  RatingLiteral,
  ImpactCost,
  IconTuple,
  NextLink,
  GrowthAmount,
  FallbackedTotalUSD,
  MarketName,
} from 'components';
import { FormattedAmountProps } from 'components/FormattedAmount/types';
import { colors, styled, SystemStyleObject } from 'core/styles';
import { ratingToLetter } from 'domain/types';
import { APOSTRO_GLOSSARY_IMPACTCOST_URL } from 'env';
import { impactCostInfo } from 'domain/utils';
import { MarketsTableData } from 'api/selectors/pages/getMarketsTableData';

const numberFilters = ['between', 'lessThan', 'greaterThan'];

const shortFormattedAmountProps: Omit<FormattedAmountProps<Amount>, 'sum'> = {
  precision: 2,
  withSI: true,
  siPrecision: 'million',
};

const shortGrowthAmountProps: Omit<FormattedAmountProps<Amount>, 'sum'> = {
  precision: 2,
  symbolSize: 'inherit',
};

const headCellAlignStyles: SystemStyleObject = {
  '& .Mui-TableHeadCell-Content': {
    justifyContent: 'flex-end',
  },
};

function ImpactCostTooltipTitle() {
  return (
    <span>
      {impactCostInfo}&nbsp;–{' '}
      <LinkInTooltip href={APOSTRO_GLOSSARY_IMPACTCOST_URL} target="_blank">
        learn more
      </LinkInTooltip>{' '}
    </span>
  );
}

export const columns: MRT_ColumnDef<MarketsTableData>[] = [
  {
    header: 'Rating',
    accessorFn: originalRow => originalRow.rating.fold(ratingToLetter, () => undefined),
    id: 'rating',
    columnFilterModeOptions: ['fuzzy', 'contains', 'startsWith', 'endsWith'],
    filterFn: 'fuzzy',
    enableColumnActions: false,
    size: 100,
    sortingFn: 'basic',
    invertSorting: true,
    Cell: ({ row }) => <RatingLiteral rating={row.original.rating} colored />,
    muiTableHeadCellProps: {
      sx: {
        paddingLeft: '12px',
      },
    },
    muiTableBodyCellProps: {
      align: 'left',
      sx: {
        paddingLeft: '12px',
        fontSize: '20px',
      },
    },
  },
  {
    header: 'Market',
    accessorKey: 'name',
    columnFilterModeOptions: ['fuzzy', 'contains', 'startsWith', 'endsWith'],
    filterFn: 'fuzzy',
    enableColumnActions: false,
    size: 170,
    Cell: ({ row }) => <MarketName marketName={row.original.name} maxWidth={140} />,
  },
  {
    header: 'Utilization',
    accessorFn: originalRow => amountToAccessorValue(originalRow.utilizationRatio),
    id: 'utilizationRatio',
    columnFilterModeOptions: numberFilters,
    filterFn: 'greaterThan',
    enableColumnActions: false,
    sortingFn: 'basic',
    size: 155,
    Cell: ({ row }) => (
      <FormattedAmount sum={row.original.utilizationRatio} precision={2} symbolSize="inherit" />
    ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Impact Cost',
    Header: () => (
      <HeaderBox>
        <InfoTooltip title={<ImpactCostTooltipTitle />} />
        <span>Impact Cost</span>
      </HeaderBox>
    ),
    accessorFn: originalRow => amountToAccessorValue(originalRow.impactCost),
    id: 'impactCost',
    columnFilterModeOptions: numberFilters,
    filterFn: 'greaterThan',
    enableColumnActions: false,
    sortingFn: 'basic',
    size: 205,
    Cell: ({ row }) => (
      <ImpactCost
        reserveTagSpace
        cost={row.original.impactCost}
        model={row.original.impactCostModel}
      />
    ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Borrows',
    accessorFn: originalRow => amountToAccessorValue(originalRow.totalDebt.value),
    id: 'totalBorrow',
    columnFilterModeOptions: numberFilters,
    filterFn: 'greaterThan',
    enableColumnActions: false,
    sortingFn: 'basic',
    size: 113,
    Cell: ({ row }) => (
      <FallbackedTotalUSD
        {...row.original.totalDebt}
        formattedAmountProps={shortFormattedAmountProps}
      />
    ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: '24H',
    accessorFn: originalRow =>
      amountToAccessorValue(originalRow.growth24H && originalRow.growth24H.map(m => m.debt)),
    id: '24Hborrow',
    columnFilterModeOptions: numberFilters,
    filterFn: 'greaterThan',
    enableColumnActions: false,
    sortingFn: 'diffs',
    size: 84,
    Cell: ({ row }) => (
      <GrowthAmount
        growth={row.original.growth24H && row.original.growth24H.map(m => m.debt)}
        formattedAmountProps={shortGrowthAmountProps}
      />
    ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Collateral',
    accessorFn: originalRow => amountToAccessorValue(originalRow.totalCollateral.value),
    id: 'totalCollateral',
    columnFilterModeOptions: numberFilters,
    filterFn: 'greaterThan',
    enableColumnActions: false,
    sortingFn: 'basic',
    size: 134,
    Cell: ({ row }) => (
      <FallbackedTotalUSD
        {...row.original.totalCollateral}
        formattedAmountProps={shortFormattedAmountProps}
      />
    ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: '24H',
    accessorFn: originalRow =>
      amountToAccessorValue(originalRow.growth24H && originalRow.growth24H.map(m => m.collateral)),
    id: '24Hcollateral',
    columnFilterModeOptions: numberFilters,
    filterFn: 'greaterThan',
    enableColumnActions: false,
    sortingFn: 'diffs',
    size: 84,
    Cell: ({ row }) => (
      <GrowthAmount
        growth={row.original.growth24H && row.original.growth24H.map(m => m.collateral)}
        formattedAmountProps={shortGrowthAmountProps}
      />
    ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Supply',
    accessorFn: originalRow => amountToAccessorValue(originalRow.totalDeposit.value),
    id: 'supplyTvl',
    columnFilterModeOptions: numberFilters,
    filterFn: 'greaterThan',
    enableColumnActions: false,
    sortingFn: 'basic',
    size: 105,
    Cell: ({ row }) => (
      <FallbackedTotalUSD
        {...row.original.totalDeposit}
        formattedAmountProps={shortFormattedAmountProps}
      />
    ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: '24H',
    accessorFn: originalRow =>
      amountToAccessorValue(originalRow.growth24H && originalRow.growth24H.map(m => m.deposit)),
    id: '24Htvl',
    columnFilterModeOptions: numberFilters,
    filterFn: 'greaterThan',
    enableColumnActions: false,
    sortingFn: 'diffs',
    size: 84,
    Cell: ({ row }) => (
      <GrowthAmount
        growth={row.original.growth24H && row.original.growth24H.map(m => m.deposit)}
        formattedAmountProps={shortGrowthAmountProps}
      />
    ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Assets',
    accessorFn: originalRow => (
      <Assets>
        <IconTuple tokens={originalRow.tokens} withTooltip />
      </Assets>
    ),
    id: 'assets',
    enableColumnActions: false,
    enableSorting: false,
    enableColumnFilter: false,
    size: 87,
    muiTableHeadCellProps: {
      align: 'center',
    },
  },
  ToggleLowSupplyFilterButton.getLowSupplyColumn(),
];

const LinkInTooltip = styled(NextLink)(() => ({
  color: colors.black,
  textDecoration: 'underline',
  transition: 'opacity 200ms',

  '&:hover': {
    opacity: 0.7,
  },
}));

const Assets = styled('div')({
  fontSize: 16,
  display: 'flex',
  justifyContent: 'center',
});

const HeaderBox = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});
